import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Radio, Row, Col, Card, message } from 'antd';
import {
    LoadingOutlined
} from '@ant-design/icons';
import axios from 'axios';
import ENVIRONMENT from '../../../../environments';
import SubscriptionUpgradeManager from '../SubscriptionUpgradeManager/SubscriptionUpgradeManager';
import './HighResModal.scss';
import './AIEditorModalViewer.scss';

const HighResModal = ({ isOpen, onClose, imageData }) => {
    const [orientation, setOrientation] = useState('square');
    const [resolution, setResolution] = useState('4k');
    const [isLoading, setIsLoading] = useState(false);
    const [requestStatus, setRequestStatus] = useState(null);
    const [cardStatus, setCardStatus] = useState(0);
    const [subscriptionUpgradeManagerModel, setSubscriptionUpgradeManagerModel] = useState(false);
    const [pendingRequest, setPendingRequest] = useState(false);
    const [imageWidth, setImageWidth] = useState(null);
    const [imageHeight, setImageHeight] = useState(null);

    // Extract width and height from renders list based on render_id
    useEffect(() => {
        if (imageData && imageData.render_id && imageData.renders && Array.isArray(imageData.renders)) {
            const matchingRender = imageData.renders.find(render => render.render_id === imageData.render_id);
            if (matchingRender) {
                setImageWidth(matchingRender.width);
                setImageHeight(matchingRender.height);
            }
        }
    }, [imageData]);
    
    useEffect(() => {
        if (isOpen) {
            loadUserRequestStatus();
        }
    }, [isOpen]);

    // Auto-proceed with request once status is loaded if there's a pending request
    useEffect(() => {
        if (pendingRequest && requestStatus !== null) {
            setPendingRequest(false);
            // Now proceed with the actual request check
            if (requestStatus === "allowed" || requestStatus === "always_allowed") {
                performHighResGeneration();
            } else {
                setIsLoading(false);
                setSubscriptionUpgradeManagerModel(true);
            }
        }
    }, [requestStatus, pendingRequest]);

    const loadUserRequestStatus = () => {
        let payload = {
            "username": localStorage.getItem('username'),
            "request_type": "ai_image_4k",
            "action": "check",
            "category": ""
        }
        axios.post(ENVIRONMENT.PAYMENT_GATEWAY, payload)
            .then(res => {
                console.log('4K Image request status:', res.data)
                setRequestStatus(res.data['request_allowed']);
                setCardStatus(res.data['card_status']);
            })
            .catch(error => {
                console.error('Error checking request status:', error);
                setRequestStatus('update_subscription'); // Default to requiring upgrade on error
            });
    };

    const handleHighResConfirm = (options) => {
        // If request status hasn't been loaded yet, load it first
        if (requestStatus === null) {
            setIsLoading(true);
            setPendingRequest(true); // Mark that we have a pending request
            loadUserRequestStatus();
            // The actual request will be triggered after status is loaded via useEffect
            return;
        }

        if (requestStatus === "allowed" || requestStatus === "always_allowed") {
            performHighResGeneration();
        }
        else {
            setSubscriptionUpgradeManagerModel(true);
        }
    };

    // Calculate scaled dimensions based on target max dimension
    const calculateScaledDimensions = (targetMax) => {
        if (!imageWidth || !imageHeight) {
            return { width: targetMax, height: targetMax };
        }
        
        const maxDimension = Math.max(imageWidth, imageHeight);
        
        // For 2K, if image is already larger than 2048, return original dimensions
        if (targetMax === 2048 && maxDimension > 2048) {
            return {
                width: imageWidth,
                height: imageHeight
            };
        }
        
        const scale = targetMax / maxDimension;
        
        return {
            width: Math.round(imageWidth * scale),
            height: Math.round(imageHeight * scale)
        };
    };

    // Get display text for resolution options
    const get4KResolution = () => {
        const dims = calculateScaledDimensions(4096);
        return `4K (${dims.width}x${dims.height})`;
    };

    const get2KResolution = () => {
        const dims = calculateScaledDimensions(2048);
        return `2K (${dims.width}x${dims.height})`;
    };

    const performHighResGeneration = () => {
        console.log("*****", imageData.src || imageData.selectedImageUrl)
        const username = localStorage.getItem('username');
        const companyId = localStorage.getItem('company_id');
        setIsLoading(true);
        // Convert resolution string to scale integer
        const scale = resolution === '4k' ? 4 : 2;
        
        let request_payload = {
            "action": "trigger_final_image_generation_job",
            "base_image_id": imageData.render_id || imageData.id,
            "username": username,
            "company_id": companyId,
            "scale": scale,
            "orientation": orientation
        }

        let gateway_payload = {
            "username": username,
            "request_type": "ai_image_4k",
            "action": "perform",
            "request_payload": request_payload,
            "category": "",
            "request_id": Date.now().toString()
        }

        console.log('Gateway payload:', gateway_payload)
        axios.post(ENVIRONMENT.PAYMENT_GATEWAY, gateway_payload).then((res) => {
            console.log(res);
            setIsLoading(false)
            onClose();
            
            // Check if action_response exists in the response
            if (res.data && res.data.action_response) {
                // Redirect to final images page using values from action_response
                const actionResponse = res.data.action_response;
                
                // Check if skipped_upscale key exists and is true
                if (actionResponse && Object.prototype.hasOwnProperty.call(actionResponse, 'upscale_request_status') && actionResponse.upscale_request_status == 'completed') {
                    window.location.href = window.location.origin + '/images?type=final_images&upscale_request_status=completed';
                } else if (actionResponse && actionResponse.new_image_id) {
                    // Only redirect with new_image_id if it exists
                    window.location.href = window.location.origin + '/images?type=final_images&new_image_id=' + actionResponse.new_image_id + '&image_src=' + (imageData.src || imageData.selectedImageUrl || imageData.url || imageData.imageUrl);
                } else {
                    // If actionResponse exists but doesn't have expected properties
                    window.location.href = window.location.origin + '/images?type=final_images';
                }
            } else {
                // Show error message if action_response is not present
                message.error('Failed to generate high-res image. Please try again.');
            }
        }).catch((error) => {
            console.error('Error generating high-res image:', error);
            setIsLoading(false);
            message.error('An error occurred while generating high-res image.');
        });
    };

    return (
        <>
            <Modal
                className='high-res-modal'
                visible={isOpen}
                onCancel={onClose}
                title={<span className="manrope f-20 lh-28 w-500 black-085 justify-in-start">
                    Save As Final
                </span>}
                closable={true}
                footer={[
                    <div className='justify-in-end'>
                        <Button className='manrope f-14 w-500 lh-22 ai-action-btn pd-small' onClick={onClose}>Cancel</Button>
                        <Button className='manrope f-14 w-500 lh-22 ai-action-btn-blue pd-small' onClick={handleHighResConfirm}>
                            {isLoading && <LoadingOutlined />}
                            Confirm & Download
                        </Button>
                    </div>
                ]}
                centered={true}>
                <Row>
                    <Col span={24} className='direction-flex col j-start-a-start'>
                        <div className='manrope f-14 w-700 lh-22 black-085'>Resolution</div>
                        <Radio.Group value={resolution} onChange={(e) => setResolution(e.target.value)}>
                            <Radio value="4k" className='manrope f-14 w-400 lh-22 black-085'>{get4KResolution()}</Radio>
                            <Radio value="2k" className='manrope f-14 w-400 lh-22 black-085'>{get2KResolution()}</Radio>
                        </Radio.Group>
                    </Col>
                </Row>
            </Modal>
            <SubscriptionUpgradeManager
                subscriptionUpgradeManagerModel={subscriptionUpgradeManagerModel}
                setSubscriptionUpgradeManagerModel={setSubscriptionUpgradeManagerModel}
                cardStatus={cardStatus}
                loadUserRequestStatus={loadUserRequestStatus}
                requestType='ai_image_4k'
            />
        </>
    );
};

export default HighResModal;
